/**
 * @fileoverview WebSocket sync client used by `replicationFactory` to power
 *               bidirectional real-time sync between Dexie and the API
 *               server (system-api, tenant-api, mobile-api).
 * @module local-db/replication/websocket-sync-client
 *
 * Responsibilities:
 * - Build and cache one `WebSocketClient` per `(apiBaseUrl, departmentId,
 *   syncRoutePrefix)` triple.
 * - Subscribe to allowed collections, dispatch wakeup events into the
 *   replication factory, and surface connection state into
 *   `syncStatusStore` (for UI badges).
 * - Provide `pushChange` so local writes can wake the server immediately
 *   instead of waiting for the next polling tick.
 *
 * Dependencies:
 * - `../websocket` — low-level `WebSocketClient` + `SyncEvent` shape.
 * - `../stores/syncStatusStore` — reactive UI status flags.
 *
 * Architecture:
 * - Server counterpart: `api/packages/core/src/websocket/WebSocketManager.ts`
 *   plus the per-app WS routes (`system-api/wsRoutes`, `tenant-api/wsRoutes`,
 *   `mobile-api/wsRoutes`).
 * - Three independent WebSockets can be active concurrently in tenant-web:
 *   `/sync` (tenant DB), `/system-sync` (system DB tenant projection), plus
 *   the auth/notification socket. Each gets a distinct cache key.
 *
 * @errorHandling
 * - Connection state changes are mirrored into `syncStatusStore` so the
 *   UI badge stays accurate. Reconnects are retried by the underlying
 *   `WebSocketClient`. Stale clients are cleaned up by `closeWebSocketSyncClient`.
 *
 * @logging
 * - Logs subscribe/unsubscribe and wakeup events with the cache key so a
 *   tenant-web instance running dual sync can be diagnosed independently.
 */

import { WebSocketClient, SyncEvent } from '../websocket';
import type { PushChange } from '../websocket/types';
import {
  setConnected,
  setRealtimeConnected,
  setConnectionState,
  updateLastPing,
} from '../stores/syncStatusStore';
import { syncLog } from '../stores/syncActivityLog';
import { debugLog, debugWarn } from '../utils/debugLog';

const _activeClients = new Map<string, WebSocketClient>();

export function buildWebSocketSyncClientKey(apiBaseUrl: string, departmentId: string, syncRoutePrefix = '/sync'): string {
  return `${apiBaseUrl}|${departmentId}|${syncRoutePrefix}`;
}

export function getActiveWebSocketSyncClient(apiBaseUrl: string, departmentId: string, syncRoutePrefix = '/sync'): WebSocketClient | null {
  return _activeClients.get(buildWebSocketSyncClientKey(apiBaseUrl, departmentId, syncRoutePrefix)) ?? null;
}

export interface WebSocketSyncClientConfig {
  apiBaseUrl: string;
  departmentId: string;
  getAuthHeaders: () => Promise<Record<string, string>>;
  onSyncEvent: (event: SyncEvent, dexieKey: string) => void;
  onReconnect: () => void;
  onStatusChange?: (status: 'live' | 'polling' | 'disconnected') => void;
  collectionMap: Record<string, string>; // dexie → server collection name
  syncRoutePrefix?: string;
  /**
   * Forward to WebSocketClient — when true the client listens for
   * `visibilitychange` to preemptively close/reopen the socket on
   * page-hide/show (needed for iOS WKWebView freeze). Defaults to false;
   * only mobile-pwa should enable this.
   */
  enableVisibilityReconnect?: boolean;
  /** Called when the server rejects the WebSocket bearer token. */
  onAuthFailure?: (reason: string) => void;
}

export function startWebSocketSyncClient(
  config: WebSocketSyncClientConfig,
): () => void {
  // Build reverse map: serverCollection → dexieCollection
  const serverToDexie = new Map<string, string>(
    Object.entries(config.collectionMap).map(([dexie, server]) => [server, dexie]),
  );

  let wsClient: WebSocketClient | null = null;
  let stopped = false;
  let wasDisconnected = false;
  let hasEverConnected = false;
  const syncRoutePrefix = config.syncRoutePrefix ?? '/sync';
  const clientKey = buildWebSocketSyncClientKey(config.apiBaseUrl, config.departmentId, syncRoutePrefix);

  const initialize = async () => {
    if (stopped) return;

    try {
      // Parse base URL to get WebSocket URL
      // Support relative paths like '/api' by resolving against window.location
      const apiUrl = new URL(config.apiBaseUrl, window.location.origin);
      const wsProtocol = apiUrl.protocol === 'https:' ? 'wss:' : 'ws:';
      // Preserve pathname (e.g., /api) so WebSocketClient can append /ws to make /api/ws
      const wsUrl = `${wsProtocol}//${apiUrl.host}${apiUrl.pathname}`;

      // Create WebSocket client with token provider function
      // This ensures a fresh token is fetched on each connection/reconnection
      wsClient = new WebSocketClient({
        url: wsUrl,
        token: async () => {
          debugLog('[ws-sync] Fetching fresh auth token...');
          const authHeaders = await config.getAuthHeaders().catch((err) => {
            console.error('[ws-sync] Failed to get auth headers:', err);
            return {};
          });
          const token = (authHeaders as any).Authorization?.replace('Bearer ', '') ?? '';
          if (!token) {
            console.warn('[ws-sync] No auth token available');
          }
          return token;
        },
        tenantSlug: config.departmentId,
        reconnectIntervalMs: 1000,
        maxReconnectDelayMs: 30000,
        heartbeatIntervalMs: 15000,
        messageTimeoutMs: 30000,
        syncRoutePrefix,
        enableVisibilityReconnect: config.enableVisibilityReconnect,
        onAuthFailure: config.onAuthFailure,
      });
      _activeClients.set(clientKey, wsClient);

      // Subscribe to connection status changes
      wsClient.onStatusChange((status) => {
        const isConnected = status.state === 'connected';
        const wasReconnecting = wasDisconnected && isConnected;

        if (isConnected) {
          hasEverConnected = true;
          debugLog('[ws-sync] Connected');
          config.onStatusChange?.('live');
          setRealtimeConnected(true, 'websocket');
          setConnected(true);
          syncLog.success(wasReconnecting ? 'WebSocket reconnected' : 'WebSocket connected to server');

          debugLog(wasReconnecting
            ? '[ws-sync] Reconnected — triggering divergence check'
            : '[ws-sync] Connected — triggering divergence check');
          config.onReconnect();
          wasDisconnected = false;
        } else if (status.state === 'reconnecting' || status.state === 'connecting') {
          if (!wasDisconnected) {
            console.warn('[ws-sync] Connection lost — attempting reconnect');
            syncLog.warn('WebSocket connection lost — reconnecting…');
            config.onStatusChange?.('polling');
            setRealtimeConnected(false, 'websocket');
            wasDisconnected = true;
          }
        } else if (status.state === 'disconnected' || status.state === 'failed') {
          if (hasEverConnected) {
            console.warn('[ws-sync] Disconnected');
            syncLog.warn('WebSocket disconnected from server');
          }
          config.onStatusChange?.('disconnected');
          setRealtimeConnected(false, 'websocket');
          wasDisconnected = true;
        }
      });

      // Subscribe to sync events
      wsClient.onSyncEvent((event) => {
        // Update ping timestamp on any message
        updateLastPing();

        const dexieKey = serverToDexie.get(event.collection) ?? event.collection;
        
        debugLog(`[ws-sync] Received sync event: ${event.type} ${event.collection}/${event.documentId || 'unknown'}`);
        debugLog(`[ws-sync] Forwarding to replicationFactory with dexieKey: ${dexieKey}`);
        
        // Forward to replicationFactory's event handler
        config.onSyncEvent(event, dexieKey);
        
        debugLog(`[ws-sync] onSyncEvent handler called for ${dexieKey}`);
      });

      // Subscribe to all collections in the map
      const collections = Array.from(new Set(Object.values(config.collectionMap)));
      wsClient.subscribe(collections);

      // Connect
      wsClient.connect();

      debugLog('[ws-sync] WebSocket sync client initialized');
    } catch (error: any) {
      console.error('[ws-sync] Failed to initialize:', error);
      if (!stopped) {
        setTimeout(initialize, 5000);
      }
    }
  };

  // Start initialization
  initialize();

  // Return cleanup function
  return () => {
    stopped = true;
    _activeClients.delete(clientKey);
    if (wsClient) {
      wsClient.disconnect();
      wsClient = null;
    }
    config.onStatusChange?.('disconnected');
    setRealtimeConnected(false, 'websocket', 'disconnected');
  };
}

// WebSocket-based pull using WebSocket client instead of HTTP fetch
export async function pullViaWebSocket(
  client: WebSocketClient | null,
  collection: string,
  checkpoint: { updatedAt: string; id?: string } | null,
  options: {
    limit?: number;
    full?: boolean;
    inspectionAccountIds?: string[];
  } = {},
): Promise<{
  documents: any[];
  checkpoint: { updatedAt: string; id?: string };
  hasMore: boolean;
  allIds?: string[];
} | null> {
  if (!client) {
    console.warn('[ws-sync] WebSocket client not available for pull');
    return null;
  }

  try {
    const result = await client.pull({
      collection,
      since: checkpoint?.updatedAt,
      sinceId: checkpoint?.id,
      limit: options.limit || 500,
      full: options.full,
      inspectionAccountIds: options.inspectionAccountIds,
    });

    return {
      documents: result.documents,
      checkpoint: result.checkpoint as { updatedAt: string; id?: string },
      hasMore: result.hasMore,
      allIds: result.allIds,
    };
  } catch (error: any) {
    if (error?.message === 'WebSocket disconnected') {
      console.debug('[ws-sync] Pull cancelled (WebSocket disconnected)');
    } else {
      console.error('[ws-sync] Pull failed:', error);
    }
    throw error;
  }
}

// WebSocket-based push using WebSocket client instead of HTTP fetch
export async function pushViaWebSocket(
  client: WebSocketClient | null,
  collection: string,
  changes: PushChange[],
): Promise<{ conflicts?: any[]; acked?: Array<{ _id: string; updatedAt?: string }> } | null> {
  if (!client) {
    console.warn('[ws-sync] WebSocket client not available for push');
    return null;
  }

  try {
    const result = await client.push({
      collection,
      changes,
    });

    return result;
  } catch (error: any) {
    if (error?.message === 'WebSocket disconnected') {
      console.debug('[ws-sync] Push cancelled (WebSocket disconnected)');
    } else {
      console.error('[ws-sync] Push failed:', error);
    }
    throw error;
  }
}
